<template>
  <div class="h3 mb-3">Сопоставление типов туриста</div>
  <p class="paragraph-s_h mb-10">Проверьте корректность сопоставления типов туристов</p>
  <e-alert v-if="touristsTypesMapping?.touristTypesRefreshRequired" variant="error" class="mb-10">
    <div>
      <p class="paragraph-m_h mb-1 font-600">Есть разрывы между возрастами детей</p>
      <p class="paragraph-s_h text-secondary">
        Такие разрывы недопустимы. Исправьте возрасты детей в
        <a :href="integration?.externalIntegration?.integrationUrl || undefined" target="_blank" class="link">
          {{ channelManagerCode === ChannelManagerCode.TravelLine ? 'TravelLine' : 'Exely' }}
        </a>
        и&nbsp;обновите их
      </p>
      <e-btn
        variant="link"
        class="mt-1"
        prepend-icon-src="refresh"
        prepend-icon-size="16"
        size="M"
        :loading="isRefreshing"
        @click="refresh"
        >Обновить возрасты детей</e-btn
      >
    </div>
  </e-alert>
  <e-form v-model="isValid" ref="formRef" class="d-flex flex-column row-gap-4">
    <e-select
      v-model="data.determineAgeMethod"
      :items="determineAgeMethods"
      :rules="[required()]"
      item-title="text"
      item-value="value"
      label="Правила расчёта возраста туриста"
    >
      <template #item="{ item }">
        <div class="input-text text-black">
          {{ item.title }}
        </div>
        <div class="paragraph-xs text-secondary">
          {{ item.raw.description }}
        </div>
      </template>
    </e-select>
    <e-input
      v-model:number-value="data.min"
      label="Минимальный возраст заезда"
      disabled
      :rules="[required()]"
      :mask="agesMask"
    />
    <e-input
      v-if="data.max"
      v-model="data.max"
      :disabled="!!touristsTypesMapping?.ageSettings.noSeatAge"
      :rules="[required()]"
      :mask="agesMask"
      label="Максимальный возраст туриста без места"
    />

    <e-data-table v-if="touristsTypesMapping?.ageSettings?.categories" :headers="headers" :items="items" />

    <teleport v-if="isMounted" defer :to="`#${travelLineIntegrationSubmitButtonTeleport}`">
      <e-btn
        append-icon-src="arrowRight"
        :loading="isLoading"
        :disabled="touristsTypesMapping?.touristTypesRefreshRequired"
        variant="primary"
        @click="submit"
      >
        Далее
      </e-btn>
    </teleport>
  </e-form>
</template>

<script setup lang="ts">
import { useMounted } from '@vueuse/core';
import { ref, toRef } from 'vue';
import { EAlert, EBtn, EDataTable, EForm, EInput, ESelect } from '@/core/components/ui';
import { determineAgeMethods } from '@/core/consts/collections';
import { agesMask } from '@/core/consts/masks';
import { travelLineIntegrationSubmitButtonTeleport } from '@/core/consts/teleport';
import { required } from '@/core/utils/validation';
import { ChannelManagerCode } from '@/graphql/generated';
import { useTravelLineStore } from '@/parts/tariffs/store/travelLine';

const emit = defineEmits<{
  (event: 'next'): void;
}>();

const isMounted = useMounted();

const { integration, updateTouristTypes, refreshTouristTypes, channelManagerCode } = useTravelLineStore();

const touristsTypesMapping = toRef(() => integration.value?.master?.steps.touristTypesMapping);

const data = ref({
  min: touristsTypesMapping.value?.ageSettings?.initialAge ?? null,
  max: touristsTypesMapping.value?.ageSettings?.noSeatAge ?? null,
  determineAgeMethod: touristsTypesMapping.value?.ageSettings?.determineAgeMethod || null,
});

const isRefreshing = ref(false);

const refresh = async () => {
  isRefreshing.value = true;
  await refreshTouristTypes();
  isRefreshing.value = false;
};

const formRef = ref();
const isValid = ref(false);
const isLoading = ref(false);

const submit = async () => {
  formRef.value?.validate();

  if (!isValid.value) {
    return;
  }

  isLoading.value = true;

  const result = await updateTouristTypes({
    determineAgeMethod: data.value.determineAgeMethod!,
  });

  if (!result.errors) {
    emit('next');
  }

  isLoading.value = false;
};

const headers = [
  {
    key: 'typeTL',
    title: channelManagerCode.value === ChannelManagerCode.TravelLine ? 'Возраст детей TL' : 'Возраст детей Exely',
  },
  {
    key: 'typeExtranet',
    title: 'Возраст детей Экстранет',
  },
];

const items = toRef(
  () =>
    touristsTypesMapping.value?.ageSettings?.categories?.values.map((item) => ({
      typeTL: `${item.from}-${item.to}`,
      typeExtranet: `${item.from}-${item.to}`,
    })) || [],
);
</script>

<style scoped lang="scss"></style>
